import * as maptilersdk from '@maptiler/sdk';
import { Root } from 'react-dom/client';
import { logger } from './logger';
import { ZOOM_THRESHOLDS } from './mapUtils';

/**
 * MarkerManager - Centralized marker lifecycle management
 *
 * Responsibilities:
 * - Create and manage MapTiler markers
 * - Track React roots and ensure proper cleanup
 * - Track DOM event listeners and remove them on cleanup
 * - Prevent memory leaks from orphaned roots and listeners
 *
 * Key Concepts:
 * - Each marker has a popup with a React root
 * - Each marker element has DOM event listeners
 * - Both must be explicitly cleaned up to prevent memory leaks
 */

interface MarkerMetadata {
  marker: maptilersdk.Marker;
  popupRoot: Root;
  eventListeners: EventListenerCleanup[];
  locationKey: string;
  vendorIds: number[];
  markerType: 'vendor' | 'gcal';
}

interface EventListenerCleanup {
  element: HTMLElement;
  eventType: string;
  handler: EventListener;
  options?: AddEventListenerOptions | boolean;
}

export class MarkerManager {
  private markers: Map<string, MarkerMetadata>;
  private circularTextRoots: Root[]; // Track CircularText roots separately

  constructor() {
    this.markers = new Map();
    this.circularTextRoots = [];
  }

  /**
   * Create a marker with full lifecycle tracking
   * Returns the marker and stores all cleanup metadata
   */
  createMarker(
    markerElement: HTMLElement,
    coordinates: [number, number],
    popupRoot: Root,
    locationKey: string,
    vendorIds: number[],
    markerType: 'vendor' | 'gcal' = 'vendor'
  ): maptilersdk.Marker {
    const marker = new maptilersdk.Marker({
      element: markerElement,
      anchor: 'center',
    });

    marker.setLngLat(coordinates);

    // Store metadata for cleanup
    const metadata: MarkerMetadata = {
      marker,
      popupRoot,
      eventListeners: [],
      locationKey,
      vendorIds,
      markerType,
    };

    this.markers.set(locationKey, metadata);

    logger.debug('MarkerManager: Created marker', {
      locationKey,
      vendorIds,
      totalMarkers: this.markers.size,
    });

    return marker;
  }

  /**
   * Register an event listener for cleanup tracking
   * Call this AFTER adding an event listener to an element
   */
  registerEventListener(
    locationKey: string,
    element: HTMLElement,
    eventType: string,
    handler: EventListener,
    options?: AddEventListenerOptions | boolean
  ): void {
    const metadata = this.markers.get(locationKey);
    if (!metadata) {
      logger.warn('MarkerManager: Cannot register listener - marker not found', locationKey);
      return;
    }

    metadata.eventListeners.push({
      element,
      eventType,
      handler,
      options,
    });

    logger.debug('MarkerManager: Registered event listener', {
      locationKey,
      eventType,
      totalListeners: metadata.eventListeners.length,
    });
  }

  /**
   * Register a CircularText React root for cleanup
   * These are separate from popup roots and need independent tracking
   */
  registerCircularTextRoot(root: Root): void {
    this.circularTextRoots.push(root);
    logger.debug('MarkerManager: Registered CircularText root', {
      totalRoots: this.circularTextRoots.length,
    });
  }

  /**
   * Add a marker to the map
   */
  addToMap(locationKey: string, map: maptilersdk.Map): void {
    const metadata = this.markers.get(locationKey);
    if (!metadata) {
      logger.warn('MarkerManager: Cannot add to map - marker not found', locationKey);
      return;
    }

    metadata.marker.addTo(map);
  }

  /**
   * Get a marker by location key
   */
  getMarker(locationKey: string): maptilersdk.Marker | null {
    return this.markers.get(locationKey)?.marker || null;
  }

  /**
   * Get all markers as an array (for compatibility with existing code)
   */
  getAllMarkers(): maptilersdk.Marker[] {
    return Array.from(this.markers.values()).map(m => m.marker);
  }

  /**
   * Get markers filtered by type
   */
  getAllMarkersByType(type: 'vendor' | 'gcal'): maptilersdk.Marker[] {
    return Array.from(this.markers.values())
      .filter(m => m.markerType === type)
      .map(m => m.marker);
  }

  /**
   * Get marker by vendor ID
   */
  getMarkerByVendorId(vendorId: number): maptilersdk.Marker | null {
    for (const metadata of this.markers.values()) {
      if (metadata.vendorIds.includes(vendorId)) {
        return metadata.marker;
      }
    }
    return null;
  }

  /**
   * Get marker count
   */
  getCount(): number {
    return this.markers.size;
  }

  /**
   * Clean up a single marker with full memory cleanup
   */
  private cleanupMarker(metadata: MarkerMetadata): void {
    const { marker, popupRoot, eventListeners, locationKey } = metadata;

    // 1. Remove all event listeners
    eventListeners.forEach(({ element, eventType, handler, options }) => {
      try {
        element.removeEventListener(eventType, handler, options);
      } catch (error) {
        logger.warn('MarkerManager: Error removing event listener', error);
      }
    });

    // 2. Unmount React root
    try {
      // Defer unmount out of React's synchronous commit cycle
      const rootToUnmount = popupRoot;
      Promise.resolve().then(() => {
        try {
          rootToUnmount.unmount();
        } catch (error) {
          logger.warn('MarkerManager: Error unmounting popup root (deferred)', error);
        }
      });
    } catch (error) {
      logger.warn('MarkerManager: Error scheduling root unmount', error);
    }

    // 3. Remove marker from map
    try {
      marker.remove();
    } catch (error) {
      logger.warn('MarkerManager: Error removing marker', error);
    }

    logger.debug('MarkerManager: Cleaned up marker', {
      locationKey,
      listenersRemoved: eventListeners.length,
    });
  }

  /**
   * Clean up markers of a specific type — use instead of cleanupAll() when only
   * one layer needs recreation (e.g. vendor markers refresh without nuking gcal markers).
   */
  cleanupByType(type: 'vendor' | 'gcal'): void {
    const toRemove: string[] = [];
    this.markers.forEach((metadata, key) => {
      if (metadata.markerType === type) {
        this.cleanupMarker(metadata);
        toRemove.push(key);
      }
    });
    toRemove.forEach(key => this.markers.delete(key));

    logger.debug('MarkerManager: cleanupByType complete', { type, removed: toRemove.length });
  }

  /**
   * Clean up all markers - call this before recreating markers
   */
  cleanupAll(): void {
    logger.debug('MarkerManager: Starting cleanup', {
      markerCount: this.markers.size,
      circularTextRootCount: this.circularTextRoots.length,
    });

    // Clean up all markers
    this.markers.forEach(metadata => {
      this.cleanupMarker(metadata);
    });

    // Clean up CircularText roots
    this.circularTextRoots.forEach(root => {
      const rootToUnmount = root;
      Promise.resolve().then(() => {
        try {
          rootToUnmount.unmount();
        } catch (error) {
          logger.warn('MarkerManager: Error unmounting CircularText root (deferred)', error);
        }
      });
    });

    // Clear all tracking
    this.markers.clear();
    this.circularTextRoots = [];

    logger.debug('MarkerManager: Cleanup complete');
  }

  /**
   * Remove a specific marker by location key
   */
  removeMarker(locationKey: string): void {
    const metadata = this.markers.get(locationKey);
    if (!metadata) {
      logger.warn('MarkerManager: Cannot remove - marker not found', locationKey);
      return;
    }

    this.cleanupMarker(metadata);
    this.markers.delete(locationKey);

    logger.debug('MarkerManager: Removed marker', {
      locationKey,
      remainingMarkers: this.markers.size,
    });
  }

  /**
   * Mutate zoom-dependent styles on existing marker elements.
   * No markers are created or destroyed — DOM elements are updated in place.
   *
   * @param getStyles - Function returning a style object given (isPopupOpen, isFree)
   * @param getIsFree - Function returning whether a vendor is a free event, given vendorIds
   * @param openMarkerId - The vendor ID whose popup is currently open (from PopupManager)
   */
  applyZoomStyles(
    getStyles: (isPopupOpen: boolean, isFree: boolean) => Record<string, string>,
    getIsFree: (vendorIds: number[]) => boolean,
    openMarkerId: number | string | null,
    zoom: number
  ): void {
    this.markers.forEach(metadata => {
      const { marker, vendorIds, markerType } = metadata;
      // GCal marker zoom visibility is managed via display:none/flex in the ZOOM STYLE effect
      if (markerType === 'gcal') return;
      const markerElement = marker.getElement();
      const visualMarker = markerElement?.querySelector('.marker-visual') as HTMLElement | null;
      const circularText = markerElement?.querySelector(
        '.circular-text-container'
      ) as HTMLElement | null;
      if (!visualMarker) return;

      const isPopupOpen =
        openMarkerId !== null &&
        typeof openMarkerId === 'number' &&
        vendorIds.includes(openMarkerId);
      const isFree = getIsFree(vendorIds);
      const styles = getStyles(isPopupOpen, isFree);
      Object.assign(visualMarker.style, styles);

      if (circularText) {
        if (zoom >= ZOOM_THRESHOLDS.DETAILED_VIEW) {
          circularText.classList.remove('hidden');
          circularText.classList.add('visible');
        } else {
          circularText.classList.remove('visible');
          circularText.classList.add('hidden');
        }
      }
    });

    logger.debug('MarkerManager: Applied zoom styles', { markerCount: this.markers.size });
  }

  /**
   * Mutate opacity on existing marker elements to implement filter dimming.
   * No markers are created or destroyed.
   *
   * @param activeVendorIds - Set of vendor IDs that should be full opacity.
   *   Pass null to restore all markers to full opacity (no active filter).
   */
  applyDimming(activeVendorIds: Set<number> | null): void {
    const DIMMED_OPACITY = '0.25';
    const FULL_OPACITY = '1';

    this.markers.forEach(metadata => {
      const { marker, vendorIds, markerType } = metadata;
      // GCal markers represent the user's own events — never dimmed by list filters
      if (markerType === 'gcal') return;
      const markerElement = marker.getElement();
      const visualMarker = markerElement?.querySelector('.marker-visual') as HTMLElement | null;
      const circularText = markerElement?.querySelector(
        '.circular-text-container'
      ) as HTMLElement | null;
      if (!visualMarker) return;

      const opacity =
        activeVendorIds === null
          ? FULL_OPACITY
          : vendorIds.some(id => activeVendorIds.has(id))
            ? FULL_OPACITY
            : DIMMED_OPACITY;

      visualMarker.style.opacity = opacity;
      if (circularText) circularText.style.opacity = opacity;
    });

    logger.debug('MarkerManager: Applied dimming', {
      activeCount: activeVendorIds?.size ?? 'all',
      markerCount: this.markers.size,
    });
  }

  /**
   * Get debug information
   */
  getDebugInfo(): {
    markerCount: number;
    circularTextRootCount: number;
    totalEventListeners: number;
  } {
    let totalListeners = 0;
    this.markers.forEach(m => {
      totalListeners += m.eventListeners.length;
    });

    return {
      markerCount: this.markers.size,
      circularTextRootCount: this.circularTextRoots.length,
      totalEventListeners: totalListeners,
    };
  }
}

/**
 * Singleton instance for use throughout the app
 */
export const markerManager = new MarkerManager();
